import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Container, Grid, Stack, Typography } from '@material-ui/core';
import { useParams, useLocation } from 'react-router-dom';
import Page from '../components/Page';
import Breadcrumb from '../components/common/Breadcrumb';
import DeploymentsKpiRow from '../components/billing/DeploymentsKpiRow';
import DeploymentsTableCard from '../components/billing/DeploymentsTableCard';
import { toBaseDeploymentRow, DEPLOYMENT_TABLE_COLUMNS } from '../utils/billing';

PartNumberTracking.propTypes = {
  teamId: PropTypes.string,
  partNumber: PropTypes.string
};

export default function PartNumberTracking({ teamId: teamIdProp, partNumber: partNumberProp }) {
  // Route params are read via useParams; props are accepted for tests/storybook.
  const params = useParams();
  const location = useLocation();
  const teamId = teamIdProp || params.teamId;
  const partNumber = partNumberProp || params.partNumber;
  const { companyId } = params;

  // The team usage report passes the raw `billable_part` (which carries the
  // `deployments` array) plus the team's display name via React Router
  // state. We don't re-fetch — see plan: the page is reachable only via the
  // team report, so a direct URL hit isn't a supported flow.
  const { partData, teamName } = location.state || {};

  const deployments = useMemo(
    () =>
      Array.isArray(partData?.deployments)
        ? partData.deployments.map((d) => ({ ...toBaseDeploymentRow(d) }))
        : [],
    [partData]
  );

  const displayTeamName = teamName || teamId;

  if (!partData) {
    return (
      <Page title="Part Number Not Found | Virtual Racks">
        <Container maxWidth={false}>
          <Typography variant="h4">Part number not found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Open this page from a team&apos;s usage report — the deployment details are passed along
            with the navigation.
          </Typography>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={`${partNumber} Deployment Tracking | Virtual Racks`}>
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          {/* Breadcrumb on the company-scoped URL renders
              Home → Billing → Company → Team → Part Number; on the legacy
              URL it renders Home → Team Usage → Team → Part Number. The
              `company`, `team`, and `part` route segments are delimiters
              and excluded from the trail so they aren't clickable. */}
          <Grid item xs={12}>
            <Breadcrumb
              labels={{
                [teamId]: displayTeamName,
                [partNumber]: partNumber
              }}
              exclude={companyId ? ['company', 'team', 'part'] : ['part']}
              sx={{ mb: 2 }}
            />
          </Grid>

          {/* Page header */}
          <Grid item xs={12}>
            <Stack>
              <Typography variant="overline" color="primary">
                Part Number Deployment
              </Typography>
              <Typography variant="h4">{partNumber}</Typography>
            </Stack>
          </Grid>

          {/* KPI cards */}
          <Grid item xs={12}>
            <DeploymentsKpiRow partNumber={partNumber} deployments={deployments} />
          </Grid>

          {/* Deployments table */}
          <Grid item xs={12}>
            <DeploymentsTableCard
              headLabel={DEPLOYMENT_TABLE_COLUMNS}
              deployments={deployments}
              description="Historical and active VRack deployments associated with this part number."
              defaultOrderBy="createdDate"
              defaultOrder="desc"
            />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
